import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  CircularProgress, 
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useMediaQuery,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  Card,
  CardContent,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { BRAND } from '../../config/brand';
import AddIcon from '@mui/icons-material/Add';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import DownloadIcon from '@mui/icons-material/Download';
import LanguageIcon from '@mui/icons-material/Language';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import ReceiptIcon from '@mui/icons-material/Receipt';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { ImageUploadService } from '../accounting/inventory/services/imageUploadService';
import AddQRMenuModal from './modal/AddQRMenuModal';
import QRMenuSettingsModal from './modal/QRMenuSettingsModal';
import QRMenuDomainModal from './modal/QRMenuDomainModal';
import AddTableModal from './modal/AddTableModal';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import { mainApi } from '../../config/apiClient';
import { useTranslation } from 'react-i18next';

// QR Menü Veri Tipi
interface QRMenuData {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
  moduleType?: 'qr' | 'ticket';
}

// Accounts.tsx'den alınan stil bileşenleri
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz (MainLayout varsayımı)
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  // 📱 Mobil: Sayfa scroll'u kullan, iç scroll iptal
  '@media (max-width: 1499px)': {
    height: 'auto',
    overflowY: 'visible',
    padding: theme.spacing(2),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// QRMenuPage arayüzü
interface QRMenuPageProps {
  branchId?: string;
  isManager?: boolean;
  onNavigate?: (page: string) => void;
}

type CreateMode = 'qr' | 'ticket';

const QRMenuPage: React.FC<QRMenuPageProps> = ({ branchId, isManager = false, onNavigate }) => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [qrMenus, setQRMenus] = useState<QRMenuData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [menuToDelete, setMenuToDelete] = useState<QRMenuData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingMenu, setDeletingMenu] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedMenuForSettings, setSelectedMenuForSettings] = useState<QRMenuData | null>(null);
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [customDomain, setCustomDomain] = useState<string>('');
  const [qrSuccessMessage, setQrSuccessMessage] = useState<string>('');
  const [ticketLimitWarning, setTicketLimitWarning] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [selectedMenuForTables, setSelectedMenuForTables] = useState<QRMenuData | null>(null);
  
  // 📱 Mobil 3 nokta menüsü için state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMenuForActions, setSelectedMenuForActions] = useState<QRMenuData | null>(null);
  
  // ✅ Masaüstü 3 nokta menüsü için state
  const [desktopMenuAnchor, setDesktopMenuAnchor] = useState<null | HTMLElement>(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  
  // ✅ Düzenleme modu için state
  const [isEditMode, setIsEditMode] = useState(false);
  const [menuToEdit, setMenuToEdit] = useState<QRMenuData | null>(null);
  const [createMode, setCreateMode] = useState<CreateMode>('qr');
  
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>, menu: QRMenuData) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedMenuForActions(menu);
  };
  
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };
  
  // ✅ Masaüstü 3 nokta menüsü handler'ları
  const handleDesktopMenuOpen = (event: React.MouseEvent<HTMLElement>, menu: QRMenuData) => {
    setDesktopMenuAnchor(event.currentTarget);
    setSelectedMenuForActions(menu);
  };
  
  const handleDesktopMenuClose = () => {
    setDesktopMenuAnchor(null);
  };

  const handleHeaderMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHeaderMenuAnchor(event.currentTarget);
  };

  const handleHeaderMenuClose = () => {
    setHeaderMenuAnchor(null);
  };

  const handleCreateOnlineTicket = () => {
    const existingTicketCount = qrMenus.filter(menu => menu.moduleType === 'ticket').length;
    if (existingTicketCount >= 1) {
      setTicketLimitWarning(t('qrMenuPage.errors.maxOneOnlineTicket'));
      handleHeaderMenuClose();
      return;
    }
    setIsEditMode(false);
    setMenuToEdit(null);
    setCreateMode('ticket');
    setIsAddModalOpen(true);
    handleHeaderMenuClose();
  };
  
  // ✅ Düzenleme modalını aç
  const handleEditMenu = (menu: QRMenuData) => {
    setMenuToEdit(menu);
    setIsEditMode(true);
    setCreateMode(menu.moduleType === 'ticket' ? 'ticket' : 'qr');
    setIsAddModalOpen(true);
    handleDesktopMenuClose();
    handleMobileMenuClose();
  };

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (uid) {
        try {
          const userData = await firebaseDataService.getUserData(uid);
          if (userData?.companyId) {
            setCompanyId(userData.companyId);
          } else {
            setError(t('qrMenuPage.errors.userCompanyNotFound'));
            setLoading(false);
          }
        } catch (error) {
          console.error("Şirket ID'si alınırken hata:", error);
          setError(t('qrMenuPage.errors.companyIdLoadFailed'));
          setLoading(false);
        }
      } else {
        setError(t('qrMenuPage.errors.userNotLoggedIn'));
        setLoading(false);
      }
    };
    fetchCompanyId();
  }, []);

  // QR Menüleri SQL'den yükle
  useEffect(() => {
    if (!companyId) {
      if (!ownAuthSession.getCurrentUid()) {
        // Eğer kullanıcı yoksa ve companyId bekleniyorsa, hata vermeden bekle
      } else if (companyId === null && ownAuthSession.getCurrentUid()) {
        // Kullanıcı var ama companyId null ise (bilgi bulunamadıysa)
        // setError zaten fetchCompanyId içinde ayarlanmış olmalı.
      }
      return;
    }

    const fetchQRMenusFromSQL = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`🔄 SQL'den QR menüleri yükleniyor (Şirket: ${companyId})`);
        
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
        const result = response.data;
        if (result.success && result.data) {
          console.log(`✅ SQL'den ${result.data.length} QR menü yüklendi`);
          
          let menuList: QRMenuData[] = result.data.map((menu: any) => {
            let modeFromSettings: string | null = null;
            if (menu.settings) {
              try {
                const parsedSettings = typeof menu.settings === 'string'
                  ? JSON.parse(menu.settings)
                  : menu.settings;
                modeFromSettings = parsedSettings?.mode ?? null;
              } catch {
                modeFromSettings = null;
              }
            }

            const isTicket = menu.template === 'ticket' || modeFromSettings === 'ticket';
            return {
            id: menu.id,
            name: menu.name || t('qrMenuPage.table.unnamedMenu'),
            branchId: menu.branchId || null,
            branchName: menu.branchName || (menu.branchId ? t('qrMenuPage.table.unnamedBranch') : undefined),
            moduleType: isTicket ? 'ticket' : 'qr'
          };
          });
          
          if (isManager && branchId) {
            console.log(`🔍 Manager filtresi uygulanıyor - Şube ID: ${branchId}`);
            console.log(`📋 Tüm menüler ve şube bilgileri:`, menuList.map(menu => ({
              id: menu.id,
              name: menu.name,
              branchId: menu.branchId,
              branchName: menu.branchName,
              eşleşiyor: menu.branchId === branchId
            })));
            menuList = menuList.filter(menu => 
              menu.branchId === branchId || menu.branchId === null
            );
            console.log(`🔧 Filtreleme kriteri: branchId === '${branchId}' VEYA branchId === null`);
            console.log(`✅ ${menuList.length} menü manager şubesi için filtrelendi`);
          }
          
          setQRMenus(menuList.sort((a, b) => a.name.localeCompare(b.name)));
        } else {
          console.log('ℹ️ SQL\'de henüz QR menü bulunamadı');
          setQRMenus([]);
        }
      } catch (err: any) {
        console.error('❌ SQL QR menü yükleme hatası:', err);
        if (err.response?.status === 400) {
          console.log('ℹ️ Şirket henüz SQL\'de kayıtlı değil, boş liste gösteriliyor');
          setQRMenus([]);
        } else {
          setError(t('qrMenuPage.errors.menuLoadFailed'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchQRMenusFromSQL();
  }, [companyId, refreshTrigger, branchId, isManager]);

  // Custom domain'i SQL'den yükle
  useEffect(() => {
    if (!companyId) return;

    const loadCustomDomainFromSQL = async () => {
      try {
        console.log(`🔄 SQL'den custom domain yükleniyor (Şirket: ${companyId})`);
        
        const response = await mainApi.get(`/api/QRMenu/custom-domain`);
        const result = response.data;
        console.log('🔍 [DEBUG] İlk yükleme API Response:', JSON.stringify(result, null, 2));
        
        if (result.success && result.data) {
          let savedDomain = '';
          
          if (typeof result.data === 'string') {
            savedDomain = result.data;
          } else if (result.data.customDomain) {
            savedDomain = result.data.customDomain;
          } else if (result.data.domainName) {
            savedDomain = result.data.domainName;
          } else {
            console.warn('⚠️ Beklenmeyen data yapısı:', result.data);
            savedDomain = '';
          }
          
          console.log(`✅ Custom domain yüklendi: ${savedDomain}`);
          setCustomDomain(savedDomain || '');
        } else {
          console.warn('⚠️ API success:false veya data yok:', result);
          setCustomDomain('');
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.log('ℹ️ Şirket henüz SQL\'de kayıtlı değil, boş custom domain');
          setCustomDomain('');
        } else {
          console.error('❌ Custom domain yükleme hatası:', error);
        }
      }
    };

    loadCustomDomainFromSQL();
  }, [companyId]);

  const handleOpenDeleteModal = (menu: QRMenuData) => {
    setMenuToDelete(menu);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setMenuToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteMenu = async () => {
    if (!menuToDelete || !companyId) {
      setError(t('qrMenuPage.errors.deleteMissingInfo'));
      return;
    }
    setDeletingMenu(true);
    setError(null);
    const menuIdToDelete = menuToDelete.id;

    try {
      console.log(`🔄 SQL'den QR menü siliniyor: ${menuIdToDelete} (Şirket: ${companyId})`);
      
      await mainApi.post(`/api/QRMenu/menu-definitions/${menuIdToDelete}/delete`);
      
      console.log(`✅ QR Menü SQL'den silindi: ${menuToDelete.name}`);
      
      setQRMenus(prevMenus => prevMenus.filter(m => m.id !== menuIdToDelete));
      handleCloseDeleteModal();
    } catch (error) {
      console.error("❌ QR Menü silme API hatası:", error);
      setError(t('qrMenuPage.errors.deleteFailed'));
    } finally {
      setDeletingMenu(false);
    }
  };

  const handleOpenSettingsModal = (menu: QRMenuData) => {
    setSelectedMenuForSettings(menu);
    setIsSettingsModalOpen(true);
  };

  const handleUploadProductImage = async (productId: string, file: File): Promise<string> => {
    if (!companyId) {
      throw new Error(t('qrMenuPage.errors.companyInfoMissing'));
    }
    
    try {
      // Resim Image Service'e (SQL tabanlı) yüklenir, Firebase Storage kullanılmaz.
      const uploaded = await ImageUploadService.uploadImages([file]);
      const downloadUrl = uploaded?.[0]?.originalUrl;
      if (!downloadUrl) {
        throw new Error(t('qrMenuPage.errors.imageUploadFailed'));
      }
      return downloadUrl;
    } catch (error) {
      console.error('Resim yükleme hatası:', error);
      throw new Error(t('qrMenuPage.errors.imageUploadFailed'));
    }
  };

  const handleCloseSettingsModal = () => {
    setSelectedMenuForSettings(null);
    setIsSettingsModalOpen(false);
  };

  const handleOpenTableModal = (menu: QRMenuData) => {
    setSelectedMenuForTables(menu);
    setIsTableModalOpen(true);
  };

  const handleCloseTableModal = () => {
    setSelectedMenuForTables(null);
    setIsTableModalOpen(false);
  };

  const getPublicMenuUrl = (menu: QRMenuData): string => {
    const isTicket = menu.moduleType === 'ticket';
    const publicPath = isTicket ? `ticket/${menu.id}` : `menu/${menu.id}`;

    const hostname = window.location.hostname;
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalHost) {
      // Local gelistirmede dogrudan qrmenu dev sunucusunu ac.
      return `http://localhost:3005/${publicPath}`;
    }

    if (customDomain) {
      return `https://${customDomain}/${publicPath}`;
    }

    // Prod davranisini koru.
    return `${BRAND.urls.base}/#/${isTicket ? `ticket/${menu.id}` : `qr-menu/${menu.id}`}`;
  };

  const handleViewMenu = (menu: QRMenuData) => {
    const baseUrl = getPublicMenuUrl(menu);
    
    // Yeni sekmede aç
    window.open(baseUrl, '_blank');
  };

  const handleManageTables = (menu: QRMenuData) => {
    try {
      sessionStorage.setItem('qrmenu_selected_table_menu_id', menu.id);
    } catch {
      // no-op
    }

    if (isManager && onNavigate) {
      onNavigate('qrmenu-tables');
    } else {
      navigate(`/qr-menu/tables?menuId=${encodeURIComponent(menu.id)}`);
    }
  };

  const handleDownloadQR = async (menu: QRMenuData) => {
    const baseUrl = getPublicMenuUrl(menu);
    
    const qrContent = baseUrl;
    
    // 4K kalitede QR kod için ultra yüksek çözünürlük ayarları
    const qrOptions: QRCode.QRCodeToDataURLOptions = {
      errorCorrectionLevel: 'H', // En yüksek hata düzeltme seviyesi
      type: 'image/png', // PNG daha kaliteli
      margin: 4, // Daha geniş kenar boşluğu
      width: 3840, // 4K genişlik (3840x3840)
      color: {
        dark: '#000000', // Saf siyah QR kod
        light: '#FFFFFF', // Saf beyaz arka plan
      },
    };

    try {
      console.log(`🔄 4K QR kod oluşturuluyor: ${qrContent}`);
      const qrDataUrl = await QRCode.toDataURL(qrContent, qrOptions);
      
      // Dosya adını daha açıklayıcı yap
      const fileName = `${menu.name.replace(/[^a-zA-Z0-9-_]/g, '_')}-QR-4K.png`;
      
      console.log(`✅ 4K QR kod başarıyla oluşturuldu: ${fileName}`);
      saveAs(qrDataUrl, fileName);
      
      // Başarı mesajı göster
      setError(null);
      setQrSuccessMessage(t('qrMenuPage.messages.qrDownloaded', { fileName }));
      
    } catch (err) {
      console.error('4K QR kod oluşturulurken hata:', err);
      setError(t('qrMenuPage.errors.qrCreateFailed'));
    }
  };

  const qrOnlyMenus = qrMenus.filter(menu => menu.moduleType !== 'ticket');
  const ticketMenus = qrMenus.filter(menu => menu.moduleType === 'ticket');
  const isSelectedTicketMenu = selectedMenuForActions?.moduleType === 'ticket';

  return (
    <ScrollableContent>
      {isMobile ? (
        // 📱 MOBİL GÖRÜNÜM
        <Box sx={{ mb: 2 }}>
          {/* Üst Satır: Başlık + Ana Buton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <QrCode2Icon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h1" fontWeight="bold">
                {t('qrMenuPage.mobile.title')}
              </Typography>
            </Box>
            {!isManager && (
              <Button 
                variant="contained" 
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsAddModalOpen(true)}
              >
                {t('qrMenuPage.mobile.newMenu')}
              </Button>
            )}
          </Box>
          
          {/* Alt Satır: Diğer Butonlar */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              color="primary"
              size="small"
              startIcon={<KitchenIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => {
                if (isManager && onNavigate) {
                  onNavigate('qrmenu-kitchen');
                } else {
                  navigate('/qr-menu/kitchen');
                }
              }}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              {t('qrMenuPage.actions.kitchen')}
            </Button>
            <Button 
              variant="outlined" 
              color="success"
              size="small"
              startIcon={<ReceiptIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => isManager && onNavigate ? onNavigate('qrmenu-orders') : navigate('/qr-menu/orders')}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              {t('qrMenuPage.actions.orders')}
            </Button>
            {!isManager && (
              <Button 
                variant="outlined" 
                color="info"
                size="small"
                startIcon={<RestaurantMenuIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => navigate('/qr-menu/customers')}
                sx={{ fontSize: '0.7rem', py: 0.5 }}
              >
                {t('qrMenuPage.actions.customers')}
              </Button>
            )}
            {!isManager && (
              <Button 
                variant="outlined" 
                color="secondary"
                size="small"
                startIcon={<LanguageIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={() => setIsDomainModalOpen(true)}
                sx={{ fontSize: '0.7rem', py: 0.5 }}
              >
                {t('qrMenuPage.actions.siteAddress')}
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        // 💻 MASAÜSTÜ GÖRÜNÜM
        <HeaderContainer>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QrCode2Icon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              {t('qrMenuPage.title')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isManager && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreateMode('qr');
                    setIsAddModalOpen(true);
                  }}
                >
                  {t('qrMenuPage.actions.addQrMenu')}
                </Button>
                <IconButton
                  size="small"
                  onClick={handleHeaderMenuOpen}
                  aria-label={t('qrMenuPage.aria.otherCreateOptions')}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </HeaderContainer>
      )}

      <Menu
        anchorEl={headerMenuAnchor}
        open={Boolean(headerMenuAnchor)}
        onClose={handleHeaderMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          if (isManager && onNavigate) {
            onNavigate('qrmenu-kitchen');
          } else {
            navigate('/qr-menu/kitchen');
          }
          handleHeaderMenuClose();
        }}>
          {t('qrMenuPage.menu.kitchenScreen')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (isManager && onNavigate) {
            onNavigate('qrmenu-orders');
          } else {
            navigate('/qr-menu/orders');
          }
          handleHeaderMenuClose();
        }}>
          {t('qrMenuPage.menu.tableOrders')}
        </MenuItem>
        {!isManager && (
          <MenuItem onClick={() => {
            navigate('/qr-menu/customers');
            handleHeaderMenuClose();
          }}>
            {t('qrMenuPage.menu.customers')}
          </MenuItem>
        )}
        {!isManager && (
          <MenuItem onClick={() => {
            setIsDomainModalOpen(true);
            handleHeaderMenuClose();
          }}>
            {t('qrMenuPage.menu.setSiteAddress')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleCreateOnlineTicket}>
          {t('qrMenuPage.menu.createOnlineTicket')}
        </MenuItem>
      </Menu>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* QR Menüler */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <RestaurantMenuIcon sx={{ mr: 1 }} /> {t('qrMenuPage.sections.qrMenus')}
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : qrOnlyMenus.length === 0 && !error ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {t('qrMenuPage.emptyStates.noQrMenus')}
          </Typography>
        </Paper>
      ) : !error ? (
        isMobile ? (
          // 📱 MOBİL: Kart Görünümü
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {qrOnlyMenus.map((menu) => (
              <Card key={menu.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Üst Satır: Menü Adı + 3 Nokta Menü */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestaurantMenuIcon fontSize="small" color="primary" />
                        {menu.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        📍 {menu.branchName || t('qrMenuPage.table.branchUnassigned')}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMobileMenuOpen(e, menu)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Ana Buton: Menüyü Görüntüle */}
                  <Button 
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewMenu(menu)}
                    sx={{ mt: 1 }}
                  >
                    {t('qrMenuPage.actions.viewMenu')}
                  </Button>
                </CardContent>
              </Card>
            ))}
            
          </Stack>
        ) : (
          // 💻 MASAÜSTÜ: Tablo Görünümü
          <>
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '40%' }}>{t('qrMenuPage.table.menuName')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '20%' }}>{t('qrMenuPage.table.branch')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'right', width: '40%' }}>{t('qrMenuPage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrOnlyMenus.map((menu) => (
                    <TableRow 
                      key={menu.id}
                      hover
                    >
                      <TableCell>{menu.name}</TableCell>
                      <TableCell>{menu.branchName || t('qrMenuPage.table.branchUnassigned')}</TableCell>
                      <TableCell align="right" sx={{ p: '6px' }}> 
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="contained"
                            color="primary"
                            onClick={() => handleViewMenu(menu)}
                            startIcon={<VisibilityIcon />}
                          >
                            {t('qrMenuPage.actions.view')}
                          </Button>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleDesktopMenuOpen(e, menu)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </>
        )
      ) : null}

      {/* Global 3 Nokta Menuleri (QR + Ticket) */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          if (selectedMenuForActions) handleEditMenu(selectedMenuForActions);
          handleMobileMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('qrMenuPage.rowMenu.edit')}</ListItemText>
        </MenuItem>
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleOpenTableModal(selectedMenuForActions);
            handleMobileMenuClose();
          }}>
            <ListItemIcon><TableRestaurantIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.rowMenu.addTable')}</ListItemText>
          </MenuItem>
        )}
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleManageTables(selectedMenuForActions);
            handleMobileMenuClose();
          }}>
            <ListItemIcon><TableRestaurantIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.menu.tables')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedMenuForActions) handleOpenSettingsModal(selectedMenuForActions);
          handleMobileMenuClose();
        }}>
          <ListItemIcon><SettingsIcon fontSize="small" color="secondary" /></ListItemIcon>
          <ListItemText>{t('qrMenuPage.rowMenu.settings')}</ListItemText>
        </MenuItem>
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleDownloadQR(selectedMenuForActions);
            handleMobileMenuClose();
          }}>
            <ListItemIcon><DownloadIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.rowMenu.download4kQr')}</ListItemText>
          </MenuItem>
        )}
        {!isManager && (
          <>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedMenuForActions) handleOpenDeleteModal(selectedMenuForActions);
              handleMobileMenuClose();
            }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>{t('qrMenuPage.rowMenu.delete')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        anchorEl={desktopMenuAnchor}
        open={Boolean(desktopMenuAnchor)}
        onClose={handleDesktopMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          if (selectedMenuForActions) handleEditMenu(selectedMenuForActions);
          handleDesktopMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('qrMenuPage.rowMenu.edit')}</ListItemText>
        </MenuItem>
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleOpenTableModal(selectedMenuForActions);
            handleDesktopMenuClose();
          }}>
            <ListItemIcon><TableRestaurantIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.rowMenu.addTable')}</ListItemText>
          </MenuItem>
        )}
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleManageTables(selectedMenuForActions);
            handleDesktopMenuClose();
          }}>
            <ListItemIcon><TableRestaurantIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.menu.tables')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedMenuForActions) handleOpenSettingsModal(selectedMenuForActions);
          handleDesktopMenuClose();
        }}>
          <ListItemIcon><SettingsIcon fontSize="small" color="secondary" /></ListItemIcon>
          <ListItemText>{t('qrMenuPage.rowMenu.settings')}</ListItemText>
        </MenuItem>
        {!isSelectedTicketMenu && (
          <MenuItem onClick={() => {
            if (selectedMenuForActions) handleDownloadQR(selectedMenuForActions);
            handleDesktopMenuClose();
          }}>
            <ListItemIcon><DownloadIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText>{t('qrMenuPage.rowMenu.download4kQr')}</ListItemText>
          </MenuItem>
        )}
        {!isManager && (
          <>
            <Divider />
            <MenuItem onClick={() => {
              if (selectedMenuForActions) handleOpenDeleteModal(selectedMenuForActions);
              handleDesktopMenuClose();
            }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>{t('qrMenuPage.rowMenu.delete')}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Online Bilet Sayfaları */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <RestaurantMenuIcon sx={{ mr: 1 }} /> {t('qrMenuPage.sections.onlineTicketPages')}
      </Typography>

      {!loading && ticketMenus.length === 0 && !error ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {t('qrMenuPage.emptyStates.noOnlineTicketPages')}
          </Typography>
        </Paper>
      ) : !loading && !error ? (
        isMobile ? (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {ticketMenus.map((menu) => (
              <Card key={menu.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestaurantMenuIcon fontSize="small" color="primary" />
                        {menu.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        📍 {menu.branchName || t('qrMenuPage.table.branchUnassigned')}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMobileMenuOpen(e, menu)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  
                  <Button 
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewMenu(menu)}
                    sx={{ mt: 1 }}
                  >
                    {t('qrMenuPage.actions.view')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565c0', color: 'white', width: '40%' }}>{t('qrMenuPage.table.pageName')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565c0', color: 'white', width: '20%' }}>{t('qrMenuPage.table.branch')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1565c0', color: 'white', textAlign: 'right', width: '40%' }}>{t('qrMenuPage.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticketMenus.map((menu) => (
                    <TableRow key={menu.id} hover>
                      <TableCell>{menu.name}</TableCell>
                      <TableCell>{menu.branchName || t('qrMenuPage.table.branchUnassigned')}</TableCell>
                      <TableCell align="right" sx={{ p: '6px' }}> 
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="contained"
                            color="primary"
                            onClick={() => handleViewMenu(menu)}
                            startIcon={<VisibilityIcon />}
                          >
                            {t('qrMenuPage.actions.view')}
                          </Button>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleDesktopMenuOpen(e, menu)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      ) : null}

      {/* Silme Onay Modalı */}
      <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t('qrMenuPage.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('qrMenuPage.deleteDialog.description', { name: menuToDelete?.name || '' })}{' '}
            {t('qrMenuPage.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={deletingMenu}>{t('qrMenuPage.deleteDialog.cancel')}</Button>
          <Button onClick={handleDeleteMenu} color="error" variant="contained" disabled={deletingMenu}>
            {deletingMenu ? <CircularProgress size={24} /> : t('qrMenuPage.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ekleme/Düzenleme Modalı */}
      <AddQRMenuModal 
        open={isAddModalOpen} 
        onClose={() => {
          setIsAddModalOpen(false);
          setIsEditMode(false);
          setMenuToEdit(null);
          setCreateMode('qr');
          // Menü eklendikten/güncellendikten sonra listeyi yenile
          setRefreshTrigger(prev => prev + 1);
        }} 
        companyId={companyId}
        // ✅ Düzenleme modu prop'ları
        editMode={isEditMode}
        menuToEdit={menuToEdit}
        createMode={createMode}
      />

      {/* QR Menü Ayarları Modal */}
      {isSettingsModalOpen && selectedMenuForSettings && (
        <QRMenuSettingsModal
          open={isSettingsModalOpen}
          onClose={handleCloseSettingsModal}
          menuId={selectedMenuForSettings.id}
          companyId={companyId}
          menuName={selectedMenuForSettings?.name}
          moduleType={selectedMenuForSettings?.moduleType}
          onImageUpload={handleUploadProductImage}
        />
      )}

      {/* Site Adresi Ayarlama Modal */}
      <QRMenuDomainModal
        open={isDomainModalOpen}
        onClose={() => {
          setIsDomainModalOpen(false);
          if (companyId) {
            const loadCustomDomainFromSQL = async () => {
              try {
                console.log(`🔄 SQL'den custom domain yenileniyor (Şirket: ${companyId})`);
                
                const response = await mainApi.get(`/api/QRMenu/custom-domain`);
                const result = response.data;
                console.log('🔍 [DEBUG] QRMenuPage API Response:', JSON.stringify(result, null, 2));
                
                if (result.success && result.data) {
                  let savedDomain = '';
                  
                  if (typeof result.data === 'string') {
                    savedDomain = result.data;
                  } else if (result.data.customDomain) {
                    savedDomain = result.data.customDomain;
                  } else if (result.data.domainName) {
                    savedDomain = result.data.domainName;
                  } else {
                    console.warn('⚠️ Beklenmeyen data yapısı:', result.data);
                    savedDomain = '';
                  }
                  
                  console.log(`✅ Custom domain yenilendi: ${savedDomain}`);
                  setCustomDomain(savedDomain || '');
                } else {
                  console.warn('⚠️ API success:false veya data yok:', result);
                  setCustomDomain('');
                }
              } catch (error: any) {
                if (error.response?.status === 404) {
                  console.log('ℹ️ Şirket henüz SQL\'de kayıtlı değil');
                  setCustomDomain('');
                } else {
                  console.error('❌ Custom domain yenileme hatası:', error);
                }
              }
            };
            loadCustomDomainFromSQL();
          }
        }}
        companyId={companyId}
      />

      {/* Masa Ekleme Modal */}
      {isTableModalOpen && selectedMenuForTables && companyId && (
        <AddTableModal
          open={isTableModalOpen}
          onClose={handleCloseTableModal}
          menuId={selectedMenuForTables.id}
          menuName={selectedMenuForTables.name}
          companyId={companyId}
        />
      )}

      {/* Başarı Mesajı Snackbar */}
      <Snackbar
        open={!!qrSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setQrSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setQrSuccessMessage('')} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {qrSuccessMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!ticketLimitWarning}
        autoHideDuration={4000}
        onClose={() => setTicketLimitWarning('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setTicketLimitWarning('')}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {ticketLimitWarning}
        </Alert>
      </Snackbar>
    </ScrollableContent>
  );
};

export default QRMenuPage; 